import React, { useContext } from "react";

import { AuthContext, IntlContext } from "context";
import { getCategoryVisual } from "constants/categories";
import type { CatalogService } from "types/service";
import { useProviderContact } from "hooks/useProviderContact";
import { Role } from "types/user";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { ProviderServiceBringNotes } from "./ProviderServiceBringNotes";
import { ProviderServiceOptions } from "./ProviderServiceOptions";

interface Props {
    service: CatalogService | null;
    providerFirstName: string;
    providerId: string;
    canContact?: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface DialogBodyProps {
    service: CatalogService;
    providerFirstName: string;
    providerId: string;
    canContact: boolean;
    onOpenChange: (open: boolean) => void;
}

const DialogBody: React.FC<DialogBodyProps> = ({ service, providerFirstName, providerId, canContact }) => {
    const intl = useContext(IntlContext);
    const { account, currentRole } = useContext(AuthContext);
    const handleContact = useProviderContact(service.id);
    const isOwnProfile = account?.resources?.provider?.[0]?.id === providerId;
    const showContactButton = canContact && currentRole !== Role.PROVIDER && !isOwnProfile;

    const svcCatKey = String(service.category ?? "").toLowerCase();
    const { palette, Icon } = getCategoryVisual(svcCatKey);
    const label = service.subcategory
        ? intl.formatMessage(`our_services.label.${service.subcategory}`)
        : intl.formatMessage(`category.${svcCatKey}`);

    return (
        <DialogContent className="max-w-md gap-5 sm:rounded-2xl">
            <DialogHeader className="space-y-3 text-left">
                <div className="flex items-center gap-3">
                    <Badge variant="iconTile" palette={palette} size="tileMd">
                        <Icon className="h-4 w-4" />
                    </Badge>
                    <div className="min-w-0 flex-1">
                        <DialogTitle className="text-base">{label}</DialogTitle>
                        <p className="mt-1 text-lg font-extrabold text-foreground">
                            {Number(service.price).toFixed(2)}€
                            <span className="text-sm font-normal text-muted-foreground">
                                {intl.formatMessage("provider.card.price_per_hour")}
                            </span>
                        </p>
                    </div>
                </div>
                <DialogDescription asChild>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">
                        {service.description || intl.formatMessage("provider.view.service_no_description")}
                    </p>
                </DialogDescription>
            </DialogHeader>

            <ProviderServiceOptions serviceData={service.data} />

            <ProviderServiceBringNotes
                serviceData={service.data}
                formatMessage={(key) => String(intl.formatMessage(key))}
            />

            <DialogFooter className="sm:justify-stretch">
                {showContactButton && (
                    <Button size="lg" className="w-full" onClick={handleContact}>
                        {intl.formatMessage("provider.card.contact", { firstName: providerFirstName })}
                    </Button>
                )}
            </DialogFooter>
        </DialogContent>
    );
};

export const ProviderServiceDetailDialog: React.FC<Props> = ({
    service,
    providerFirstName,
    providerId,
    canContact = true,
    open,
    onOpenChange,
}) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        {service && (
            <DialogBody
                service={service}
                providerFirstName={providerFirstName}
                providerId={providerId}
                canContact={canContact}
                onOpenChange={onOpenChange}
            />
        )}
    </Dialog>
);
